import React from 'react';
import './VenueMap.css';

/**
 * FallbackMap component that displays when Google Maps API is not available
 * Provides a simple visual representation with markers
 * 
 * @param {Object} props
 * @param {Array} props.venues - Array of venues to display
 * @param {Array} props.gigs - Array of gigs to display
 * @param {Function} props.onMarkerClick - Function to call when a marker is clicked
 */
const FallbackMap = ({ venues = [], gigs = [], onMarkerClick }) => {
  // Calculate a simple grid layout for markers
  const gridSize = Math.ceil(Math.sqrt(venues.length + gigs.length));
  const cellSize = 100 / gridSize;
  
  // Handle marker click
  const handleMarkerClick = (item) => {
    if (onMarkerClick && typeof onMarkerClick === 'function') {
      onMarkerClick(item);
    }
  };
  
  return (
    <div className="fallback-map">
      <div className="fallback-map-header">
        <h3>Map View</h3>
        <p>Google Maps is currently unavailable. Showing simplified view.</p>
        <p className="fallback-map-note">
          To enable Google Maps, please configure your API key to allow requests from your development URL.
        </p>
      </div>
      
      <div className="fallback-map-grid">
        {/* Render venue markers */}
        {venues.map((venue, index) => (
          <div 
            key={`venue-${venue.id || index}`}
            className="fallback-marker venue-marker"
            style={{
              left: `${(index % gridSize) * cellSize + cellSize/2}%`,
              top: `${Math.floor(index / gridSize) * cellSize + cellSize/2}%`,
            }}
            onClick={() => handleMarkerClick(venue)}
            title={venue.venue_name || venue.full_name || 'Venue'}
          >
            <div className="fallback-marker-label">V</div>
            <div className="fallback-marker-tooltip">
              {venue.venue_name || venue.full_name || 'Venue'}
              <br />
              {venue.location || 'No location'}
            </div>
          </div>
        ))}
        
        {/* Render gig markers */}
        {gigs.map((gig, index) => (
          <div 
            key={`gig-${gig.id || index}`}
            className="fallback-marker gig-marker"
            style={{
              left: `${((venues.length + index) % gridSize) * cellSize + cellSize/2}%`,
              top: `${Math.floor((venues.length + index) / gridSize) * cellSize + cellSize/2}%`,
            }}
            onClick={() => handleMarkerClick(gig)}
            title={gig.title || 'Gig'}
          >
            <div className="fallback-marker-label">G</div>
            <div className="fallback-marker-tooltip">
              {gig.title || 'Gig'}
              <br />
              {gig.location || 'No location'}
              <br />
              {gig.date ? new Date(gig.date).toLocaleDateString() : 'No date'}
            </div>
          </div>
        ))}
      </div>
      
      <div className="fallback-map-legend">
        <div className="legend-item">
          <div className="fallback-marker venue-marker mini">
            <div className="fallback-marker-label">V</div>
          </div>
          <span>Venue</span>
        </div>
        <div className="legend-item">
          <div className="fallback-marker gig-marker mini">
            <div className="fallback-marker-label">G</div>
          </div>
          <span>Gig</span>
        </div>
      </div>
    </div>
  );
};

export default FallbackMap;
